import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchCreditCostCenterReview,
  fetchCreditCostCenterTargets,
  saveCreditCostCenterReview,
  type CreditCostCenterReview,
  type CreditCostCenterTarget,
} from "../api/creditCostCenterReview";
import { ApiError } from "../lib/api";
import {
  costCenterTypeFromProjectKey,
  costCenterTypeLabel,
  displayCostCenterKey,
  displayCostCenterTitle,
  projectCostCenterKeyFromProjectKey,
} from "../lib/costCenterDisplay";
import DocumentPdfActions from "./DocumentPdfActions";

function sourceValue(value: string | null | undefined) {
  return value?.trim() || "Nicht angegeben";
}

function costCenterName(key: string | null) {
  if (!key) return "Nicht eindeutig";
  const type = costCenterTypeFromProjectKey(key);
  const name = displayCostCenterKey(type, type === "project" ? projectCostCenterKeyFromProjectKey(key) ?? key : key);
  return type === "project" ? name : `${name} (${costCenterTypeLabel(type)})`;
}

function InvoiceEvidence({ review }: { review: CreditCostCenterReview }) {
  const invoices = useMemo(() => {
    const grouped = new Map<string, {
      number: string | null;
      lines: CreditCostCenterReview["invoice_evidence"];
    }>();
    for (const line of review.invoice_evidence) {
      const invoice = grouped.get(line.doc_id);
      if (invoice) invoice.lines.push(line);
      else grouped.set(line.doc_id, { number: line.doc_number, lines: [line] });
    }
    return [...grouped.entries()];
  }, [review.invoice_evidence]);
  if (invoices.length === 0) {
    return <p className="text-sm text-slate-600">Keine eindeutig verknüpfte Rechnung gefunden. Die gedruckte Referenz bleibt oben sichtbar.</p>;
  }
  return <div className="space-y-3">
    {invoices.map(([docId, invoice]) => {
      const keys = [...new Set(invoice.lines.map((line) => line.project_key))];
      const addresses = [...new Set(invoice.lines.map((line) => line.delivery_address).filter(Boolean))];
      return <div key={docId} className="border-t border-slate-200 pt-3 first:border-0 first:pt-0">
        <Link to={`/belege/${encodeURIComponent(docId)}`} className="font-medium text-emerald-700 hover:underline">
          Rechnung {invoice.number ?? docId}
        </Link>
        <p className="mt-1 text-sm text-slate-700">Kostenstelle: {keys.map(costCenterName).join(" · ")}</p>
        <p className="mt-1 text-sm text-slate-700">Lieferadresse: {addresses.length ? addresses.join(" · ") : "Nicht angegeben"}</p>
        {invoice.lines[0]?.pdf_available ? <div className="mt-2"><DocumentPdfActions docId={docId} docNumber={invoice.number ?? docId} linePos={null} /></div> : null}
      </div>;
    })}
  </div>;
}

export default function CreditCostCenterReviewPanel({
  docId,
  docNumber,
  linePos,
  onClose,
}: {
  docId: string;
  docNumber: string;
  linePos: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<CreditCostCenterTarget | null>(null);
  const [reason, setReason] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const reviewKey = ["credit-cost-center-review", docId, linePos] as const;
  const reviewQuery = useQuery({
    queryKey: reviewKey,
    queryFn: () => fetchCreditCostCenterReview(docId, linePos),
  });
  const targetQuery = useQuery({
    queryKey: ["credit-cost-center-targets", search.trim()],
    queryFn: () => fetchCreditCostCenterTargets(search.trim()),
    enabled: search.trim().length >= 2,
  });
  const mutation = useMutation({
    mutationFn: (action: "assign" | "defer" | "undo") => {
      if (!reviewQuery.data) throw new Error("Position noch nicht geladen.");
      return saveCreditCostCenterReview({
        docId,
        linePos,
        requestId: crypto.randomUUID(),
        action,
        targetKey: action === "assign" ? selected?.key ?? null : null,
        reason: reason.trim(),
        expectedRevision: reviewQuery.data.revision,
        expectedBinding: reviewQuery.data.source_binding,
      });
    },
    onSuccess: (review, action) => {
      queryClient.setQueryData(reviewKey, review);
      void queryClient.invalidateQueries({ queryKey: ["belege"] });
      void queryClient.invalidateQueries({ queryKey: ["cost-centers"] });
      setSelected(null);
      setReason("");
      setMessage(action === "assign" ? "Kostenstelle zugeordnet." : action === "defer"
        ? "Die Position bleibt zur Klärung offen." : "Zuordnung zurückgenommen.");
    },
    onError: (error) => {
      setMessage(null);
      if (error instanceof ApiError && error.status === 409) {
        void reviewQuery.refetch();
      }
    },
  });
  const review = reviewQuery.data;
  const canSave = reason.trim().length >= 3 && !mutation.isPending;

  return <section id="kostenstelle-pruefen" aria-labelledby="kostenstelle-pruefen-heading" className="mt-6 scroll-mt-6 rounded-lg border border-amber-200 bg-amber-50/30 p-4 md:p-5">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 id="kostenstelle-pruefen-heading" className="text-lg font-semibold text-slate-900">Kostenstelle für Position {linePos} prüfen</h2>
        <p className="mt-1 text-sm text-slate-700">Vergleiche die Originalbelege. TruuCost wählt hier keine Kostenstelle vor.</p>
      </div>
      <button type="button" onClick={onClose} className="text-sm text-slate-600 hover:underline">Schließen</button>
    </div>
    {reviewQuery.isLoading ? <p className="mt-4 text-sm text-slate-600">Nachweise werden geladen …</p> : null}
    {reviewQuery.isError ? <p role="alert" className="mt-4 text-sm text-rose-700">Die Nachweise konnten nicht geladen werden.</p> : null}
    {review ? <>
      <p className="mt-4 border-l-2 border-amber-400 pl-3 text-sm text-amber-950">{review.reason}</p>
      {review.decision_issue ? <p role="alert" className="mt-3 text-sm text-amber-800">{review.decision_issue} Bitte prüfe die Position erneut.</p> : null}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="min-w-0 rounded-md border border-slate-200 bg-white p-4">
          <h3 className="font-semibold text-slate-900">Diese Gutschrift</h3>
          <dl className="mt-3 grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-2 break-words text-sm">
            <dt className="text-slate-500">Artikel</dt><dd>{sourceValue(review.line.article_code)} · {sourceValue(review.line.description)}</dd>
            <dt className="text-slate-500">Kundenangabe</dt><dd>{sourceValue(review.line.context_customer_ref_raw)}</dd>
            <dt className="text-slate-500">Projektcode</dt><dd>{sourceValue(review.line.context_project_code)}</dd>
            <dt className="text-slate-500">Lieferadresse</dt><dd>{sourceValue(review.line.context_delivery_address_raw)}</dd>
          </dl>
          {review.pdf_available ? <div className="mt-3"><DocumentPdfActions docId={docId} docNumber={docNumber} linePos={linePos} pageNo={Number(review.line.page_no) || null} /></div> : <p className="mt-3 text-sm text-amber-800">Original-PDF nicht verfügbar.</p>}
        </div>
        <div className="min-w-0 rounded-md border border-slate-200 bg-white p-4">
          <h3 className="font-semibold text-slate-900">{new Set(review.invoice_evidence.map((item) => item.doc_id)).size === 1 ? "Verknüpfte Rechnung" : "Verknüpfte Rechnungen"}</h3>
          <div className="mt-3"><InvoiceEvidence review={review} /></div>
        </div>
      </div>
      {review.decision_active && review.decision?.status === "assigned" ? <p className="mt-4 text-sm text-slate-700">Aktuelle Entscheidung: {costCenterName(review.decision.target_key)}. Eine neue Auswahl ersetzt sie; „Zurücknehmen“ öffnet die Zuordnung wieder.</p> : null}
      {review.decision_active && review.decision?.status === "deferred" ? <p className="mt-4 text-sm text-slate-700">Zur weiteren Klärung offen gelassen.</p> : null}
      <div className="mt-5">
        <label htmlFor="credit-cost-center-search" className="block text-sm font-medium text-slate-800">Kostenstelle auswählen</label>
        <input id="credit-cost-center-search" value={search} onChange={(event) => { setSearch(event.target.value); setSelected(null); }} placeholder="Name oder Schlüssel suchen …" className="mt-1 h-9 w-full max-w-md rounded-md border border-slate-300 bg-white px-3 text-sm" />
        {targetQuery.isFetching ? <p className="mt-2 text-sm text-slate-500">Kostenstellen werden gesucht …</p> : null}
        {search.trim().length >= 2 && !targetQuery.isFetching && targetQuery.data?.items.length === 0 ? <p className="mt-2 text-sm text-slate-500">Keine passende Kostenstelle gefunden.</p> : null}
        {targetQuery.data?.items.length ? <div role="group" aria-label="Passende Kostenstellen" className="mt-2 max-h-44 max-w-xl overflow-y-auto rounded-md border border-slate-200 bg-white">
          {targetQuery.data.items.map((target) => <button key={target.key} type="button" onClick={() => setSelected(target)} aria-pressed={selected?.key === target.key} className={`block w-full border-b border-slate-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-slate-50 ${selected?.key === target.key ? "bg-emerald-50 font-medium text-emerald-900" : "text-slate-700"}`}>
            {displayCostCenterTitle(target.type, target.key, target.label)} <span className="text-xs text-slate-500">({costCenterTypeLabel(target.type)})</span>
          </button>)}
        </div> : null}
        <label htmlFor="credit-cost-center-reason" className="mt-4 block text-sm font-medium text-slate-800">Begründung</label>
        <textarea id="credit-cost-center-reason" value={reason} onChange={(event) => setReason(event.target.value)} rows={2} maxLength={500} placeholder="Welche Angaben im Beleg stützen deine Entscheidung?" className="mt-1 w-full max-w-xl rounded-md border border-slate-300 bg-white px-3 py-2 text-sm" />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button type="button" disabled={!canSave || !selected} onClick={() => mutation.mutate("assign")} className="h-9 rounded-md bg-emerald-700 px-3 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50">Zuordnung speichern</button>
          <button type="button" disabled={!canSave} onClick={() => mutation.mutate("defer")} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">Ohne Zuordnung belassen</button>
          {review.decision_active && review.decision && review.decision.status !== "open" ? <button type="button" disabled={!canSave} onClick={() => mutation.mutate("undo")} className="text-sm text-slate-700 hover:underline disabled:opacity-50">Entscheidung zurücknehmen</button> : null}
        </div>
        {mutation.isError ? <p role="alert" className="mt-2 text-sm text-rose-700">{mutation.error instanceof ApiError ? mutation.error.message : "Die Entscheidung konnte nicht gespeichert werden."}</p> : null}
        {message ? <p role="status" className="mt-2 text-sm text-emerald-800">{message}</p> : null}
        <p className="mt-3 text-xs text-slate-600">Person, Zeitpunkt und Begründung werden gespeichert. Die gedruckten Angaben bleiben erhalten.</p>
      </div>
      {review.history.length ? <details className="mt-5 border-t border-slate-200 pt-3"><summary className="cursor-pointer text-sm font-medium text-slate-700">Bisherige Entscheidungen ({review.history.length})</summary><ol className="mt-2 space-y-2 text-sm text-slate-700">{review.history.map((event) => <li key={event.event_id}>{event.actor_label} · {new Date(event.created_at).toLocaleString("de-AT")} · {event.action === "assign" ? `Zugeteilt: ${costCenterName(event.after.target_key)}` : event.action === "defer" ? "Offen gelassen" : "Zurückgenommen"} — {event.reason}</li>)}</ol></details> : null}
    </> : null}
  </section>;
}
